import {
    NavigationExtras,
    ParamMap,
} from '@angular/router';
import { FOLDER_ID_PARAM } from '@models';
import {
    GridFiltersState,
    GridUrlParamKeys,
    IEncodedParams,
    IGridUrlParams,
    IStoredGridState,
    IUrlGrid,
} from '@pages/generics/grid/state-handlers/handler-types';
import { IDropdownOption } from '@uipath/angular/components/ui-grid';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import {
    decodeParams,
    encodeParams,
} from '@utils/encode.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import fromPairs from 'lodash-es/fromPairs';

export class UrlAdapter<TEntity> {
    constructor(private _grid: IUrlGrid<TEntity>) { }

    public extractParams(urlParamMap: ParamMap) {
        const params = {
            state: urlParamMap.get('state'),
            index: urlParamMap.get('index'),
            size: urlParamMap.get('size'),
            search: urlParamMap.get('search'),
        };

        const hasAny = Object.values(params).some(v => v != null);

        return {
            params,
            hasAny,
        };
    }

    public computeParams(
        urlParams: Record<string, string | null>,
        storageParams: IStoredGridState | null,
    ) {
        const { state, search, index: indexP, size: sizeP } = urlParams;

        const urlStateParams: IEncodedParams = state && decodeParams(state) || {};
        urlStateParams.filters = storageParams && storageParams.filters || urlStateParams.filters || {};
        urlStateParams.sort = storageParams && storageParams.sort || urlStateParams.sort;
        const index = Number(indexP);
        const size = Number(sizeP);

        return {
            ...urlStateParams,
            index: indexP !== null ? index : this._grid.pageState.pageIndex,
            size: (indexP !== null && size !== 0) ? size : storageParams ? storageParams.size : this._grid.pageState.pageSize,
            search: search || '',
        };
    }

    public storeState(replaceUrl: boolean) {
        const isSamePage = !this._grid.previousState() || this._grid.state()!.page.pageIndex === this._grid.previousState()!.page.pageIndex;
        const paramMap = this._grid.router.routerState.snapshot.root.queryParamMap;
        const navOptions: NavigationExtras = {
            queryParams: {
                ...this._paramMapWithout(paramMap, GridUrlParamKeys),
                ...this._encodeState(),
            },
            preserveFragment: true,
            replaceUrl: replaceUrl || isSamePage,
        };

        if (
            navOptions.queryParams!.hasOwnProperty(FOLDER_ID_PARAM) &&
            this._grid.appState.user.folder.hasAny
        ) {
            // At this point (i.e. outside of app initialization), the FolderManager's OU id is the source of truth
            // instead of the current URL query param.
            navOptions.queryParams![FOLDER_ID_PARAM] = this._grid.appState.user.folder.value!.id;
        }

        this._grid.router.navigate([], navOptions);
    }

    private _paramMapWithout(paramMap: ParamMap, keys: Record<string, any>): Record<string, string | null> {
        const pairs = paramMap.keys
            .filter(k => !keys[k])
            .map(k => [k, paramMap.get(k)]);

        return fromPairs(pairs);
    }

    private _encodeState() {
        const state = this._grid.state()!;

        const params: IGridUrlParams = {
            index: state.page.pageIndex,
            size: state.page.pageSize,
        };

        if (!!this._grid.header.searchValue) {
            params.search = this._grid.header.searchValue;
        }

        const encodedState: IEncodedParams = {};

        const filters = state.filters.reduce((filterMap, current) => {
            const column = this._grid.columns.find(c =>
                c.property === current.property ||
                !!c.searchableDropdown && c.searchableDropdown.property === current.property,
            );

            if (!column) { return filterMap; }

            const component = (column.searchableDropdown || column.dropdown);

            if (
                !component ||
                !component.value
            ) { return filterMap; }

            const dirtyValue = cloneDeep(component.value) as ISuggestValue & IDropdownOption;

            delete dirtyValue.loading;
            delete dirtyValue.icon;
            delete dirtyValue.translationKey;
            delete dirtyValue.isCustom;

            filterMap[current.property as string] = dirtyValue as ISuggestValue | IDropdownOption;

            return filterMap;
        }, {} as GridFiltersState);

        if (Object.keys(filters).length) {
            encodedState.filters = filters;
        }

        if (!!state.sort.field) {
            encodedState.sort = {
                field: state.sort.field as string,
                direction: state.sort.direction,
            };
        }

        if (Object.keys(encodedState).length) {
            params.state = encodeParams(encodedState);
        }

        return params;
    }
}
